'use client'

import {
  AddCommentOutlined,
  ArticleOutlined,
  AutoAwesome,
  HandshakeOutlined,
  SellOutlined,
  Send,
  Stop,
  UploadFileOutlined
} from '@mui/icons-material'
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  Drawer,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material'
import { alpha, styled, useTheme } from '@mui/material/styles'
import { noop } from 'lodash'
import { useTranslations } from 'next-intl'
import { usePathname, useRouter } from 'next/navigation'
import { type ClipboardEvent, type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useGateValue } from '~/configs/statsig'
import AssistantMarkdown from '~/features/assistant/AssistantMarkdown'
import {
  type AttachFileButtonHandle,
  AttachFileButton,
  MessageAttachmentChips,
  PendingAttachmentChips
} from '~/features/assistant/AttachmentChips'
import {
  type AssistantActivityItem,
  type AssistantActivityLabels,
  AssistantActivity,
  AssistantBubble,
  AssistantMessageStack,
  UserBubble
} from '~/features/assistant/ChatBubbles'
import ComposerContextTag from '~/features/assistant/ComposerContextTag'
import useFileDropZone from '~/features/assistant/useFileDropZone'
import ActionProposalPart from '~/features/assistant-v2/parts/ActionProposalPart'
import ClarificationCard from '~/features/generative-dashboard/ClarificationCard'
import { useCurrentOrgId } from '~/store/slices/currentOrg'
import { useBannerOffset, useSnackbarControls } from '~/store/slices/display'

import BillingCompletenessCard from './BillingCompletenessCard'
import { BILLING_COMPOSER_PANEL_WIDTH, useBillingComposerContext } from './BillingComposerProvider'
import BillingDraftCard, { type DraftApplyOutcome } from './BillingDraftCard'
import DraftCompletenessCard from './DraftCompletenessCard'
import TaxGuidanceCard from './TaxGuidanceCard'
import { pendingDraftKey, stashDraftForWizard, takePendingDraft } from './pendingDraft'
import type { BillingComposerEntity, BillingComposerMode, BillingDraft, BillingTaxGuidance } from './types'
import { type ComposerMessage, useBillingComposer } from './useBillingComposer'
import { useBillingComposerV2 } from './useBillingComposerV2'
import { useBillingSetupCardItems } from './useBillingSetupCompleteness'
import { useComposerFormAdapter } from './useComposerFormAdapter'
import useSessionScope from './useSessionScope'

/** Backend cap on one user message. */
export const MESSAGE_MAX_CHARS = 6000
/** Pasted text longer than this becomes an attached "document" chip instead of filling the input. */
export const PASTE_DOCUMENT_THRESHOLD = 2000

/** The starter prompt that needs a document: clicking it opens the file picker before seeding the input. */
const UPLOAD_PROMPT_KEY = 'prompt_upload_tariff'

const PROMPT_KEYS = {
  product_create: [
    UPLOAD_PROMPT_KEY,
    'prompt_product_create_1',
    'prompt_product_create_2',
    'prompt_product_create_3',
    'prompt_product_create_4'
  ],
  product_edit: [UPLOAD_PROMPT_KEY, 'prompt_product_edit_1', 'prompt_product_edit_2', 'prompt_product_edit_3'],
  product_view: ['prompt_view_1', 'prompt_product_view_2', UPLOAD_PROMPT_KEY],
  agreement_create: ['prompt_agreement_create_1', 'prompt_agreement_create_2', 'prompt_general_1'],
  agreement_edit: ['prompt_agreement_edit_1', 'prompt_agreement_create_2'],
  agreement_view: ['prompt_view_1', 'prompt_agreement_view_2'],
  general: [UPLOAD_PROMPT_KEY, 'prompt_general_1', 'prompt_general_2', 'prompt_general_3']
} as const

type PromptKey = (typeof PROMPT_KEYS)[keyof typeof PROMPT_KEYS][number]

function promptKeysFor(entity: BillingComposerEntity | undefined, mode: BillingComposerMode): readonly PromptKey[] {
  if (!entity) return PROMPT_KEYS.general
  return PROMPT_KEYS[`${entity}_${mode}`]
}

/**
 * The right-docked "Compose with Agent" panel. Mount it once inside a `BillingComposerProvider`
 * (and inside the wizard's `FormProvider` when the page has a form). Persistent drawer on md+ that the
 * provider makes room for; full-screen dialog on smaller screens.
 */
export default function BillingComposerPanel() {
  const { isOpen, closeComposer, entity, mode, entityId } = useBillingComposerContext()
  const theme = useTheme()
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'))
  const bannerOffset = useBannerOffset()
  const t = useTranslations('features.BillingComposer')
  const { openSnackbar } = useSnackbarControls()
  const adapter = useComposerFormAdapter(entity, mode)
  const sessionScope = useSessionScope()
  const assistantV2 = useGateValue('assistant-v2')

  // A stashed draft from a detail page or hub lands in this wizard's form on page mount,
  // even when the composer drawer is closed. Agreement edit reseeds with reset() in a parent
  // effect; child effects run first, so apply after this flush or that reset overwrites the
  // draft and the snackbar would lie.
  const pendingAppliedRef = useRef(false)
  useEffect(() => {
    if (pendingAppliedRef.current || !adapter.hasForm || !entity || !sessionScope) return
    let cancelled = false
    queueMicrotask(() => {
      if (cancelled || pendingAppliedRef.current) return
      pendingAppliedRef.current = true
      const pending = takePendingDraft(pendingDraftKey(entity, mode === 'edit' ? entityId : undefined, sessionScope))
      if (!pending) return
      const result = adapter.applyDraft(pending.draft, pending.selectedPaths)
      if (!result) return
      openSnackbar(t('pending_applied', { count: result.applied.length, skipped: result.skipped.length }), {
        severity: result.skipped.length > 0 ? 'warning' : 'success'
      })
    })
    return () => {
      cancelled = true
    }
  }, [adapter, entity, entityId, mode, openSnackbar, sessionScope, t])

  if (isMdUp) {
    return (
      <Drawer
        variant="persistent"
        anchor="right"
        open={isOpen}
        PaperProps={{
          'aria-label': t('panel_title'),
          sx: {
            width: BILLING_COMPOSER_PANEL_WIDTH,
            top: bannerOffset,
            // Leave room for layout banners above and the floating assistant bar below.
            height: `calc(100% - ${bannerOffset}px - var(--synop-assistant-bar-height, 0px))`,
            borderLeft: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        {isOpen && <PanelContent key={String(assistantV2)} assistantV2={assistantV2} onClose={closeComposer} />}
      </Drawer>
    )
  }

  return (
    <Dialog fullScreen open={isOpen} onClose={closeComposer} aria-label={t('panel_title')}>
      {isOpen && <PanelContent key={String(assistantV2)} assistantV2={assistantV2} onClose={closeComposer} />}
    </Dialog>
  )
}

type PanelContentProps = {
  /** Runs the conversation on assistant-api rather than the v1 assistant service; fixed for the mounted panel. */
  assistantV2: boolean
  onClose: () => void
}

/** Everything inside the drawer/dialog. Mounted only while open so the transcript effects run per opening. */
function PanelContent({ assistantV2, onClose }: PanelContentProps) {
  const t = useTranslations('features.BillingComposer')
  const router = useRouter()
  const pathname = usePathname()
  const organizationId = useCurrentOrgId()
  const sessionScope = useSessionScope()
  const { entity, mode, entityId, entityName, pageTitle, pendingPrompt, clearPendingPrompt } = useBillingComposerContext()
  const adapter = useComposerFormAdapter(entity, mode)

  // The parent keys this component on `assistantV2`, so the hook in use never changes while it is mounted.
  const useComposer = assistantV2 ? useBillingComposerV2 : useBillingComposer
  const composer: ReturnType<typeof useBillingComposer> & { onActionSettled?: () => void } = useComposer({
    organizationId,
    entity,
    mode,
    entityId,
    entityName,
    pathname,
    pageTitle,
    getFormSnapshot: adapter.hasForm ? adapter.getSnapshot : undefined
  })
  const {
    messages,
    drafts,
    busy,
    status,
    error,
    resuming,
    send,
    answerClarification,
    abort,
    newChat,
    resumeFromSession,
    attachments
  } = composer
  const onActionSettled = composer.onActionSettled ?? noop

  const [input, setInput] = useState('')
  const [pastedDocument, setPastedDocument] = useState<string | null>(null)
  const [includeForm, setIncludeForm] = useState(true)
  /** True once the session resume has settled, so a queued prompt cannot race the restored transcript. */
  const [ready, setReady] = useState(false)
  const transcriptEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const attachButtonRef = useRef<AttachFileButtonHandle>(null)

  const addFiles = attachments.addFiles
  const onDroppedFiles = useCallback((files: FileList) => addFiles(files), [addFiles])
  const { isDragging, dropZoneProps } = useFileDropZone({ onFiles: onDroppedFiles, disabled: !organizationId })

  // Restore the thread this page started earlier in the session, then hand the queued prompt to it.
  useEffect(() => {
    if (!organizationId) return
    void resumeFromSession().finally(() => setReady(true))
  }, [organizationId, resumeFromSession])

  useEffect(() => {
    if (!ready || !pendingPrompt || resuming || busy || !organizationId) return
    clearPendingPrompt()
    void send(pendingPrompt, { includeForm: adapter.hasForm && includeForm })
  }, [adapter.hasForm, busy, clearPendingPrompt, includeForm, organizationId, pendingPrompt, ready, resuming, send])

  const lastMessage = messages[messages.length - 1]
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ block: 'end' })
  }, [messages.length, lastMessage?.text.length, drafts.length])

  const activityLabels = useMemo<AssistantActivityLabels>(
    () => ({
      activity: t('activity'),
      collapse: t('activity_collapse'),
      completed: t('activity_completed'),
      done: t('activity_done'),
      emptyDetails: t('activity_emptyDetails'),
      expand: t('activity_expand'),
      reasoning: t('activity_reasoning'),
      running: t('activity_running'),
      skillCount: (count) => t('activity_skillCount', { count }),
      thinking: t('activity_thinking'),
      toolCount: (count) => t('activity_toolCount', { count }),
      working: t('activity_working')
    }),
    [t]
  )

  const composed = useMemo(() => composeMessage(input, pastedDocument, t('paste_separator')), [input, pastedDocument, t])
  const overCap = composed.length > MESSAGE_MAX_CHARS
  // A message may be just the attached document; the backend still needs some text with it.
  const hasText = composed.trim().length > 0
  const canSend =
    (hasText || attachments.readyCount > 0) && !attachments.isUploading && !busy && Boolean(organizationId)

  const submit = useCallback(() => {
    if (!canSend) return
    const body = hasText ? composed : t('attach_defaultMessage')
    const message = overCap ? body.slice(0, MESSAGE_MAX_CHARS) : body
    const displayText = pastedDocument ? `${input.trim()}\n${t('paste_chip', { count: pastedDocument.length })}`.trim() : undefined
    void send(message, { includeForm: adapter.hasForm && includeForm, displayText })
    setInput('')
    setPastedDocument(null)
  }, [adapter.hasForm, canSend, composed, hasText, includeForm, input, overCap, pastedDocument, send, t])

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      submit()
    }
  }

  const onPaste = (event: ClipboardEvent<HTMLDivElement>) => {
    // Screenshots / files on the clipboard become attachments rather than text.
    if (event.clipboardData.files.length > 0) {
      event.preventDefault()
      addFiles(event.clipboardData.files)
      return
    }
    const text = event.clipboardData.getData('text')
    if (text.length <= PASTE_DOCUMENT_THRESHOLD) return
    event.preventDefault()
    setPastedDocument((prev) => (prev ? `${prev}\n\n${text}` : text))
  }

  /** Starter prompts send immediately, except the upload prompt, which needs the document first. */
  const onStarterPrompt = (key: PromptKey) => {
    const prompt = t(key)
    if (key === UPLOAD_PROMPT_KEY) {
      setInput(prompt)
      attachButtonRef.current?.openPicker()
      inputRef.current?.focus()
      return
    }
    void send(prompt, { includeForm: adapter.hasForm && includeForm })
  }

  /** Whether a draft can be written into this page's form, or must travel to a wizard instead. */
  const appliesInPlace = useCallback(
    (draft: BillingDraft): boolean => {
      if (!adapter.hasForm || draft.entity !== entity) return false
      if (draft.mode === 'create' && mode === 'edit') return false
      if (draft.mode === 'update' && mode === 'create') return false
      if (draft.mode === 'update' && draft.targetId && draft.targetId !== entityId) return false
      return true
    },
    [adapter.hasForm, entity, entityId, mode]
  )

  const applyDraft = useCallback(
    (draft: BillingDraft, selectedPaths: string[] | undefined): DraftApplyOutcome => {
      if (appliesInPlace(draft)) {
        const result = adapter.applyDraft(draft, selectedPaths)
        return result ? { kind: 'applied', ...result } : { kind: 'failed' }
      }
      // The detail page / hub has no form: stash the draft and let the wizard apply it on mount.
      const route = stashDraftForWizard(
        draft,
        selectedPaths,
        sessionScope,
        draft.entity === entity ? entityId : undefined
      )
      if (!route) return { kind: 'failed' }
      router.push(route)
      return { kind: 'navigated' }
    },
    [adapter, appliesInPlace, entity, entityId, router, sessionScope]
  )

  const applyTaxGuidance = adapter.hasForm && entity === 'product' ? adapter.applyTaxGuidance : undefined
  const draftsById = useMemo(() => new Map(drafts.map((draft) => [draft.draftId, draft])), [drafts])
  const promptKeys = promptKeysFor(entity, mode)
  const isEmpty = ready && messages.length === 0
  const contextLabel = entityName?.trim() || (entity ? t(`entity_${entity}`) : t('panel_title'))
  const contextIcon =
    entity === 'product' ? <SellOutlined />
    : entity === 'agreement' ? <HandshakeOutlined />
    : <AutoAwesome />

  return (
    <Root {...dropZoneProps} data-cy="billing-composer-panel">
      {isDragging && (
        <DropOverlay data-cy="billing-composer-drop-overlay">
          <UploadFileOutlined color="primary" sx={{ fontSize: 40 }} />
          <Typography variant="subtitle2" color="primary">
            {t('attach_dropHere')}
          </Typography>
        </DropOverlay>
      )}
      <ComposerContextTag
        data-cy="billing-composer-context-tag"
        icon={contextIcon}
        label={contextLabel}
        dismissLabel={t('panel_close')}
        dismissDataCy="billing-composer-close"
        onDismiss={onClose}
        endAdornment={
          <Tooltip title={t('panel_newChat')}>
            <span>
              <IconButton size="small" onClick={newChat} disabled={busy || messages.length === 0} aria-label={t('panel_newChat')}>
                <AddCommentOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        }
      />

      <Transcript data-cy="billing-composer-transcript">
        {resuming && (
          <Stack direction="row" spacing={1} alignItems="center" color="text.secondary">
            <CircularProgress size={14} />
            <Typography variant="caption">{t('panel_resuming')}</Typography>
          </Stack>
        )}

        {isEmpty && (
          <EmptyState
            entity={entity}
            mode={mode}
            promptKeys={promptKeys}
            onPrompt={onStarterPrompt}
            disabled={!organizationId}
            onNavigate={onClose}
          />
        )}

        {messages.map((message) => (
          <MessageRow
            key={message.id}
            message={message}
            activityLabels={activityLabels}
            drafts={(message.draftIds ?? []).map((id) => draftsById.get(id)).filter(isDraft)}
            appliesInPlace={appliesInPlace}
            onApplyDraft={applyDraft}
            onApplyTaxGuidance={applyTaxGuidance}
            onAnswer={(answerText, displayText, answers) => void answerClarification(message.id, answerText, displayText, answers)}
            onActionSettled={onActionSettled}
            busy={busy}
            onNavigate={onClose}
          />
        ))}

        {busy && status && (
          <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
            {status}
          </Typography>
        )}
        {error && <Alert severity="error">{error}</Alert>}
        <div ref={transcriptEndRef} />
      </Transcript>

      <Footer>
        {attachments.attachments.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <PendingAttachmentChips attachments={attachments.attachments} onRemove={attachments.remove} disabled={busy} />
          </Box>
        )}
        {pastedDocument && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <Chip
              icon={<ArticleOutlined />}
              label={t('paste_chip', { count: pastedDocument.length })}
              onDelete={() => setPastedDocument(null)}
              size="small"
              data-cy="billing-composer-pasted-chip"
            />
          </Stack>
        )}
        {overCap && (
          <Alert severity="warning" sx={{ mb: 1, py: 0 }} data-cy="billing-composer-truncation">
            {t('paste_truncated', { max: MESSAGE_MAX_CHARS, count: composed.length })}
          </Alert>
        )}
        <Composer data-cy="billing-composer-input-shell">
          <TextField
            inputRef={inputRef}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={onKeyDown}
            onPaste={onPaste}
            placeholder={
              pastedDocument ? t('input_placeholderWithPaste')
              : attachments.readyCount > 0 ? t('input_placeholderWithAttachments')
              : t('input_placeholder')
            }
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            size="small"
            variant="standard"
            autoFocus
            disabled={!organizationId}
            InputProps={{ disableUnderline: true }}
            inputProps={{ 'aria-label': t('input_label'), 'data-cy': 'billing-composer-input' }}
          />
          <ComposerToolbar>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <AttachFileButton ref={attachButtonRef} onFiles={addFiles} disabled={!organizationId || busy} />
              {adapter.hasForm ?
                <FormControlLabel
                  sx={{ ml: 0, mr: 0 }}
                  control={<Switch size="small" checked={includeForm} onChange={(_, checked) => setIncludeForm(checked)} />}
                  label={
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {t('input_includeForm')}
                    </Typography>
                  }
                />
              : canSend ?
                <Typography variant="caption" color="text.disabled" noWrap>
                  {t('input_enterHint')}
                </Typography>
              : isEmpty && attachments.attachments.length === 0 && !pastedDocument ?
                <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0 }}>
                  {t('attach_hint')}
                </Typography>
              : null}
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              {composed.length > MESSAGE_MAX_CHARS * 0.8 && (
                <Typography
                  variant="caption"
                  color={overCap ? 'warning.main' : 'text.disabled'}
                  data-cy="billing-composer-char-count"
                >
                  {t('input_charCount', { count: composed.length, max: MESSAGE_MAX_CHARS })}
                </Typography>
              )}
              {busy ?
                <Tooltip title={t('input_stop')}>
                  <ActionButton
                    color="error"
                    onClick={abort}
                    aria-label={t('input_stop')}
                    data-cy="billing-composer-stop"
                  >
                    <Stop fontSize="small" />
                  </ActionButton>
                </Tooltip>
              : <Tooltip title={t('input_send')}>
                  <span>
                    <ActionButton
                      color="primary"
                      onClick={submit}
                      disabled={!canSend}
                      aria-label={t('input_send')}
                      data-cy="billing-composer-send"
                    >
                      <Send fontSize="small" />
                    </ActionButton>
                  </span>
                </Tooltip>
              }
            </Stack>
          </ComposerToolbar>
        </Composer>
      </Footer>
    </Root>
  )
}

type EmptyStateProps = {
  entity: BillingComposerEntity | undefined
  mode: BillingComposerMode
  promptKeys: readonly PromptKey[]
  onPrompt: (key: PromptKey) => void
  disabled: boolean
  onNavigate: () => void
}

function EmptyState({ entity, mode, promptKeys, onPrompt, disabled, onNavigate }: EmptyStateProps) {
  const t = useTranslations('features.BillingComposer')
  const intro = entity ? t(`intro_${entity}_${mode}`) : t('intro_general')

  return (
    <Stack spacing={2} data-cy="billing-composer-empty">
      <AssistantBubble sx={{ maxWidth: '100%' }}>{intro}</AssistantBubble>
      <Stack spacing={1}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {t('prompts_heading')}
        </Typography>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {promptKeys.map((key) => (
            <Chip
              key={key}
              label={t(key)}
              variant="outlined"
              icon={key === UPLOAD_PROMPT_KEY ? <UploadFileOutlined /> : undefined}
              onClick={() => onPrompt(key)}
              disabled={disabled}
              sx={{ height: 'auto', py: 0.5, '& .MuiChip-label': { whiteSpace: 'normal' } }}
            />
          ))}
        </Stack>
      </Stack>
      {!entity && <SetupCompleteness onNavigate={onNavigate} />}
    </Stack>
  )
}

/** Client-side checklist shown in general mode before the first turn, so the panel is useful with no model call. */
function SetupCompleteness({ onNavigate }: { onNavigate: () => void }) {
  const { score, items, isLoading } = useBillingSetupCardItems()
  return <BillingCompletenessCard score={score} items={items} isLoading={isLoading} dense onNavigate={onNavigate} />
}

type MessageRowProps = {
  message: ComposerMessage
  activityLabels: AssistantActivityLabels
  drafts: BillingDraft[]
  appliesInPlace: (draft: BillingDraft) => boolean
  onApplyDraft: (draft: BillingDraft, selectedPaths: string[] | undefined) => DraftApplyOutcome
  onApplyTaxGuidance?: (guidance: BillingTaxGuidance) => boolean
  onAnswer: (answerText: string, displayText: string, answers: Record<string, string>) => void
  /** Called when an action this message proposed reaches its outcome. */
  onActionSettled: () => void
  busy: boolean
  onNavigate: () => void
}

function MessageRow({
  message,
  activityLabels,
  drafts,
  appliesInPlace,
  onApplyDraft,
  onApplyTaxGuidance,
  onAnswer,
  onActionSettled,
  busy,
  onNavigate
}: MessageRowProps) {
  if (message.role === 'user') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <UserBubble>{message.text}</UserBubble>
        {message.attachments && <MessageAttachmentChips attachments={message.attachments} />}
      </Box>
    )
  }

  const activityItems: AssistantActivityItem[] =
    message.toolCalls?.map((toolCall) => ({
      id: toolCall.id,
      label: toolCall.summary || toolCall.name,
      detail: toolCall.summary ? toolCall.name : undefined,
      kind: 'tool' as const,
      status: toolCall.status
    })) ?? []
  const isActive = Boolean(message.streaming || activityItems.some((item) => item.status === 'running'))

  return (
    <Stack spacing={1}>
      <AssistantMessageStack
        activityFirst={!isActive}
        content={
          message.text ? (
            <AssistantBubble sx={{ maxWidth: '100%', minWidth: 0 }}>
              <AssistantMarkdown>{message.text}</AssistantMarkdown>
            </AssistantBubble>
          ) : null
        }
        activity={<AssistantActivity active={isActive} items={activityItems} labels={activityLabels} reasoning={message.thinking} />}
      />
      {drafts.map((draft) => (
        <Stack key={draft.draftId} spacing={1}>
          <BillingDraftCard
            draft={draft}
            appliesInPlace={appliesInPlace(draft)}
            onApply={(selectedPaths) => onApplyDraft(draft, selectedPaths)}
            disabled={busy}
          />
          {draft.taxGuidance && (
            <TaxGuidanceCard guidance={draft.taxGuidance} onApply={onApplyTaxGuidance} disabled={busy} />
          )}
          {draft.completeness && <DraftCompletenessCard completeness={draft.completeness} onNavigate={onNavigate} />}
        </Stack>
      ))}
      {message.proposals?.map((proposal) => (
        <ActionProposalPart key={proposal.actionId} proposal={proposal} readOnly={false} onSettled={onActionSettled} />
      ))}
      {message.clarification && (
        <ClarificationCard
          intro={message.clarification.intro}
          questions={message.clarification.questions}
          answeredLabels={message.clarificationAnswers}
          retired={message.clarificationRetired}
          disabled={busy}
          onSubmit={onAnswer}
        />
      )}
    </Stack>
  )
}

/** Joins the typed message and the pasted document the way the model should read them. */
export function composeMessage(input: string, pastedDocument: string | null, separator: string): string {
  const typed = input.trim()
  if (!pastedDocument) return typed
  return typed ? `${typed}\n\n${separator}\n${pastedDocument}` : `${separator}\n${pastedDocument}`
}

function isDraft(draft: BillingDraft | undefined): draft is BillingDraft {
  return draft !== undefined
}

const Root = styled(Box)({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  minHeight: 0
})

const DropOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  pointerEvents: 'none',
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  border: `2px dashed ${theme.palette.primary.main}`,
  borderRadius: theme.shape.borderRadius
}))

const Transcript = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  padding: theme.spacing(2)
}))

const Footer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  borderTop: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper
}))

/** Unified input + toolbar shell so the composer reads as one control, not a field stacked on a hint row. */
const Composer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.75),
  padding: theme.spacing(1.25, 1.25, 1, 1.25),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  transition: theme.transitions.create(['border-color', 'box-shadow'], {
    duration: theme.transitions.duration.shorter
  }),
  '&:focus-within': {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.16)}`
  }
}))

const ComposerToolbar = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  minWidth: 0
})

/** Filled send/stop control — clearer affordance than a ghost icon next to pale hint text. */
const ActionButton = styled(IconButton)(({ theme }) => ({
  width: 32,
  height: 32,
  flexShrink: 0,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark
  },
  '&.MuiIconButton-colorError': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.error.dark
    }
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.action.disabledBackground,
    color: theme.palette.action.disabled
  }
}))
